import React, { useState, Fragment, useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider/';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { insertColumn, isColumnInColumnConfiguration } from 'ui-components/services/grid-column-helper-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import ConfirmDisableSmartRouting from './confirmation-disable-smart-routing-modal';
import ConfirmEnableSmartRouting from './confirmation-enable-smart-routing-modal';
import { fulfillmentStrategies } from './constants';
import messages from './messages';
import { ProductsGridViewIds, InventoryGridViewIds } from '../../../constants/grid-view-ids';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { useModal } from '../../../hooks/use-modal';
import {
  useSmartRoutingSellerSetting,
  useSmartRoutingSetupCompleteSellerSetting,
  useSelectedFulfillmentStrategySellerSetting,
  useFulfillmentProvidersForAutoRoutingSellerSetting,
} from '../../../hooks/use-seller-setting';
import { LogService } from '../../../services/log-service';
import Global from '../../../store/global-selectors';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { saveUserSettingGridConfig } from '../../../store/user/user.actions';
import {
  PRODUCT_MODAL_TYPE,
  CHOOSE_IMPORT_DIALOGUE,
  IMPORT_IN_PROGRESS_DIALOGUE,
  IDENTIFY_BY_SKU,
} from '../../products-page/products/constants/import-products';
import FileUploader from '../../products-page/products/modals/import/import-file-uploader';
import ImportLogModal from '../../products-page/products/modals/import-log/import-log-modal';
import { HelpArticleLink } from '../../../components/help-article-link';
import { linkIDs } from '../../../services/link-service/links';
import { getInventorySettingsPath } from '../../../routes/constants';
import { updateConfiguration } from '../api/smart-routing-rules';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import {
  baseColumnsDefaultConfigs as inventoryBaseColumnsDefaultConfigs,
  ActiveShippingLocationsColumnId,
} from '../../../store/inventory/grid/default-column-configuration';
import { baseColumnsDefaultConfigs as productBaseColumnsDefaultConfigs } from '../../../store/products/grid/default-column-configuration';
import { useUserSetting } from '../../../hooks/use-user-setting';
import { importProducts } from '../../../store/products/grid/product.grid.actions';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';

import style from './style.module.less';
import commonStyles from '../settings-page.module.less';

/** @typedef { import('./types').AutoRoutingConfiguration}  AutoRoutingConfiguration */

export const SmartRoutingRules = () => {
  const { importLogModalOpen } = useSelector((state) => state.products);
  const importProductsStarted = useSelector(Global.selectProductproductImportStarted);
  const importProductsComplete = useSelector(Global.selectProductproductImportCompleted);

  const isSmartRoutingEnabled = useSmartRoutingSellerSetting();
  const selectedFulfillmentStrategy = useSelectedFulfillmentStrategySellerSetting();
  const isSmartRoutingSetupComplete = useSmartRoutingSetupCompleteSellerSetting();
  const shouldUseFulfillmentProviders = useFulfillmentProvidersForAutoRoutingSellerSetting();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [modalDialogue, setModalDialogue] = useState(CHOOSE_IMPORT_DIALOGUE);

  const identifyBy = IDENTIFY_BY_SKU;
  const overwriteExisting = true;
  const modalType = PRODUCT_MODAL_TYPE;
  const {
    close: closeConfirmDisableSmartRoutingModal,
    open: openConfirmDisableSmartRouting,
    isOpen: isConfirmDisableSmartRoutingModalOpen,
  } = useModal();
  const {
    close: closeConfirmEnableSmartRoutingModal,
    open: openConfirmEnableSmartRouting,
    isOpen: isConfirmEnableSmartRoutingModalOpen,
  } = useModal();
  const [isLoading, setIsLoading] = useAsyncSafeState(false);

  const userProductGridColumnConfiguration = useUserSetting(UserSettings.productGridDefaultConfig.settingName);
  const userInventoryGridColumnConfiguration = useUserSetting(UserSettings.inventoryGridDefaultConfig.settingName);

  const onConfirm = useCallback(() => {
    dispatch(
      importProducts({
        file,
        identifyBy,
        overwriteExisting,
        importType: modalType,
      }),
    );
    setModalDialogue(IMPORT_IN_PROGRESS_DIALOGUE);
  }, [dispatch, file, identifyBy, overwriteExisting, modalType]);

  /**
   * Calls the API to save the auto-routing configuration
   * @param {AutoRoutingConfiguration} [config]
   * @returns nothing - fires off a dispatch that will display a Notification
   * once the changes have been saved successfull or an error notification if there was problem
   */
  const saveConfiguration = useCallback(
    (config) => {
      updateConfiguration(config)
        .then(() => {
          setIsLoading(false);

          dispatch(
            saveNewSellerSettings({
              [SellerSettings.selectedFulfillmentStrategy.key]: config.selectedFulfillmentStrategy,
              [SellerSettings.isSmartRoutingEnabled.key]: config.enableSmartOrderRouting,
              [SellerSettings.isSmartOrderRoutingSetupComplete.key]: true,
              [SellerSettings.shouldUseFulfillmentProviders.key]: config.shouldUseFulfillmentProviders,
            }),
          );

          // if the seller has enabled smart routing and they have custom column configurations
          // for the Product Grid and/or Inventory Grid, we need to add the "Active Shipping Locations" column
          // to the user's custom column configuration if it doesn't already exist. They only reason the column may exist
          // is in a scenario where the seller has disabled smart routing, then enabled it again.
          // <param name="columnToInsert">The column from the grid's default column configuration that need to be inserted into the user's custom configuration</param>
          // <param name="updatedUserGridColumnConfiguration">The user's custom column configuration with the columnToInsert added</param>
          if (
            userProductGridColumnConfiguration !== undefined &&
            !isColumnInColumnConfiguration(ActiveShippingLocationsColumnId, userProductGridColumnConfiguration)
          ) {
            const columnToInsert = productBaseColumnsDefaultConfigs.filter((column) => column.id === ActiveShippingLocationsColumnId)[0];
            const updatedUserGridColumnConfiguration = insertColumn(columnToInsert, userProductGridColumnConfiguration);
            dispatch(
              saveUserSettingGridConfig(
                ProductsGridViewIds.PRODUCTS,
                ProductsGridViewIds.PRODUCTS,
                ProductsGridViewIds.DEFAULT,
                updatedUserGridColumnConfiguration,
              ),
            );
          }

          if (
            userInventoryGridColumnConfiguration !== undefined &&
            !isColumnInColumnConfiguration(ActiveShippingLocationsColumnId, userInventoryGridColumnConfiguration)
          ) {
            const columnToInsert = inventoryBaseColumnsDefaultConfigs.filter((column) => column.id === ActiveShippingLocationsColumnId)[0];
            const updatedUserGridColumnConfiguration = insertColumn(columnToInsert, userInventoryGridColumnConfiguration);
            dispatch(
              saveUserSettingGridConfig(
                InventoryGridViewIds.INVENTORY,
                InventoryGridViewIds.INVENTORY,
                InventoryGridViewIds.DEFAULT,
                updatedUserGridColumnConfiguration,
              ),
            );
          }

          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            content: <FormattedMessage {...messages.save} />,
            duration: 3000,
          });
        })
        .catch((error) => {
          setIsLoading(false);
          LogService.logError(error);
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: <FormattedMessage {...messages.failedToSave} />,
            duration: 3000,
          });
        });
    },
    [dispatch, setIsLoading, userInventoryGridColumnConfiguration, userProductGridColumnConfiguration],
  );

  /**
   * Calls the API to save the auto-routing configuration
   * @param {string} [val] fulfillment strategy id: FulfillmentStrategyClosest, FulfillmentStrategyFewestShipments
   * @returns nothing - the call to saveConfiguration fires off a dispatch that will display a Notification
   * once the changes have been saved successfull or an error notification if there was problem
   */
  const onSelectedFulfillmentStrategyChange = useCallback(
    (val) => {
      setIsLoading(true);
      /** @type {AutoRoutingConfiguration} */
      const config = {
        enableSmartOrderRouting: isSmartRoutingEnabled,
        activeShippingLocations: [],
        selectedFulfillmentStrategy: val,
        shouldUseFulfillmentProviders,
      };
      saveConfiguration(config);
    },
    [setIsLoading, saveConfiguration, isSmartRoutingEnabled, shouldUseFulfillmentProviders],
  );

  /**
   * Calls the API to save the auto-routing configuration
   * @param {string} [val] true/false indicating if Auto-Routing should use external fulfillment providers
   * @returns nothing - the call to saveConfiguration fires off a dispatch that will display a Notification
   * once the changes have been saved successfull or an error notification if there was problem
   */
  const onShouldUseFulfillmentProvidersChange = useCallback(
    (val) => {
      setIsLoading(true);
      /** @type {AutoRoutingConfiguration} */
      const config = {
        enableSmartOrderRouting: isSmartRoutingEnabled,
        activeShippingLocations: [],
        selectedFulfillmentStrategy,
        shouldUseFulfillmentProviders: val,
      };
      saveConfiguration(config);
    },
    [setIsLoading, saveConfiguration, isSmartRoutingEnabled, selectedFulfillmentStrategy],
  );

  /**
   * Calls the API to save the auto-routing configuration
   * @param {string} [val] true/false indicating if Auto-Routing is enabled for the seller
   * @returns nothing - the call to saveConfiguration fires off a dispatch that will display a Notification
   * once the changes have been saved successfull or an error notification if there was problem
   */
  const handleSaveConfiguration = useCallback(
    (val) => {
      setIsLoading(true);
      /** @type {AutoRoutingConfiguration} */
      const config = {
        enableSmartOrderRouting: val,
        activeShippingLocations: [],
        selectedFulfillmentStrategy,
        shouldUseFulfillmentProviders,
      };
      saveConfiguration(config);
    },
    [setIsLoading, saveConfiguration, selectedFulfillmentStrategy, shouldUseFulfillmentProviders],
  );

  const handleSmartRoutingChange = useCallback(() => {
    if (isSmartRoutingSetupComplete && !isSmartRoutingEnabled) {
      handleSaveConfiguration(true);
      return;
    }

    if (isSmartRoutingEnabled) {
      openConfirmDisableSmartRouting();
    } else {
      openConfirmEnableSmartRouting();
    }
  }, [
    isSmartRoutingEnabled,
    openConfirmDisableSmartRouting,
    openConfirmEnableSmartRouting,
    isSmartRoutingSetupComplete,
    handleSaveConfiguration,
  ]);

  const handleContinueDisablingSmartRouting = useCallback(() => {
    closeConfirmDisableSmartRoutingModal();
    handleSaveConfiguration(false);
  }, [closeConfirmDisableSmartRoutingModal, handleSaveConfiguration]);

  const handleContinueEnablingSmartRouting = useCallback(() => {
    closeConfirmEnableSmartRoutingModal();
    handleSaveConfiguration(false);
  }, [closeConfirmEnableSmartRoutingModal, handleSaveConfiguration]);

  const isSellerCurrentlySetupForAutoRouting = !isSmartRoutingSetupComplete && isSmartRoutingEnabled;
  if (isSellerCurrentlySetupForAutoRouting) {
    dispatch(
      saveNewSellerSettings({
        [SellerSettings.isSmartOrderRoutingSetupComplete.key]: true,
      }),
    );
  }
  const isAutoRoutingConfigurationComplete = isSmartRoutingSetupComplete || isSellerCurrentlySetupForAutoRouting;

  return (
    <Fragment>
      <div className={classnames(commonStyles.content, style.content)}>
        <div className={style['top-content']}>
          <SettingsPageTitle value={messages.header} />
          <Text
            formattedMessageValues={{
              learnMoreLink: (chunks) => <HelpArticleLink articleId={linkIDs.OrderRouting}>{chunks}</HelpArticleLink>,
            }}
            textType={TextType.Description}
            value={messages.TurnOnAutomatedOrderRoutingDescription}
          />
        </div>
        {isLoading ? (
          <div className={style.container}>
            <LoadingIndicator className={style['loading-indicator']} theme='grey' />
          </div>
        ) : null}

        {!isLoading && (
          <div className={style['settings-page-section-content']}>
            {!isAutoRoutingConfigurationComplete && (
              <>
                <div className={style['settings-page-section']}>
                  <Text textType={TextType.H4} value={messages.sectionTitleSetupAutoRoutingMultipleShippingLocations} />
                </div>
                <div className={classnames(style['settings-page-section'], style['sub-text-container'])}>
                  <Text textType={TextType.Description} value={messages.sectionSubtextSetupAutoRoutingMultipleShippingLocations} />
                </div>
                <ul className={classnames(style['auto-routing-bulleted-list'], style['sub-text-container'])}>
                  <li className={style['auto-routing-bulleted-list-item']}>
                    <Text textType={TextType.Description} value={messages.sectionSubtextSetupAutoRoutingMultipleShippingLocationsBullet1} />
                  </li>
                  <li className={style['auto-routing-bulleted-list-item']}>
                    <Text
                      formattedMessageValues={{
                        link: (chunks) => (
                          <Button className={style['auto-routing-setup-link']} href='/settings/warehouses' theme='link'>
                            {chunks}
                          </Button>
                        ),
                      }}
                      textType={TextType.Description}
                      value={messages.sectionSubtextSetupAutoRoutingMultipleShippingLocationsBullet2}
                    />
                  </li>
                  <li className={style['auto-routing-bulleted-list-item']}>
                    <Text textType={TextType.Description} value={messages.sectionSubtextSetupAutoRoutingMultipleShippingLocationsBullet3} />
                  </li>
                  <li className={style['auto-routing-bulleted-list-item']}>
                    <Text textType={TextType.Description} value={messages.sectionSubtextSetupAutoRoutingMultipleShippingLocationsBullet4} />
                  </li>
                </ul>
                <div className={style['settings-page-section']}>
                  <Text textType={TextType.H4} value={messages.headerDisabled} />
                </div>
                <div className={classnames(style['settings-page-section'], style['sub-text-container'])}>
                  <Text
                    formattedMessageValues={{
                      link: (chunks) => (
                        <Button className={style['auto-routing-setup-link']} href='/settings/warehouses' theme='link'>
                          {chunks}
                        </Button>
                      ),
                    }}
                    textType={TextType.Description}
                    value={messages.sectionSubtextSetupAutoRouting}
                  />
                </div>
                <div className={classnames(style['settings-page-section'], style['sub-text-container'])}>
                  <Text
                    formattedMessageValues={{
                      b: (chunks) => <span className={style.bolded}>{chunks}</span>,
                    }}
                    textType={TextType.Description}
                    value={messages.setupAutoRoutingSubText}
                  />
                </div>
                <div className={style['settings-page-section']}>
                  <Button className={style['submit-button']} onClick={handleSmartRoutingChange} theme='primary'>
                    <FormattedMessage {...messages.setupAutoRoutingButton} />
                  </Button>
                </div>
              </>
            )}
            {isAutoRoutingConfigurationComplete ? (
              <>
                <div className={classnames(style['settings-page-section'], style['sub-text-container'])}>
                  <Text textType={TextType.H4} value={messages.headerEnabled} />
                </div>
                <div className={classnames(style['settings-page-section'], style['sub-text-container'])}>
                  <InlineMessage className={style['auto-routing-info-box']} type={INLINE_MESSAGE_TYPES.info}>
                    <FlexColumn gap={Gap.Space12}>
                      <FormattedMessage
                        {...messages.setupCompleteNotificationMessage}
                        values={{
                          link: (chunks) => (
                            <Button
                              className={style['auto-routing-link']}
                              onClick={() => navigate(getInventorySettingsPath())}
                              theme='link'
                            >
                              {chunks}
                            </Button>
                          ),
                        }}
                      />
                      <FormattedMessage {...messages.orderImportWarning} />
                    </FlexColumn>
                  </InlineMessage>
                </div>
                <div className={style['settings-page-section']}>
                  <Text className={style['flex-input']} textType={TextType.Label} value={messages.header} />
                  <div className={classnames(style['flex-input'])}>
                    <CheckboxSlider checkboxId='smart-routing-toggle' checked={isSmartRoutingEnabled} onChange={handleSmartRoutingChange} />
                  </div>
                </div>
                <div className={classnames(style['settings-page-section'], style['sub-text-container'])}>
                  <Text textType={TextType.Description} value={isSmartRoutingEnabled ? messages.enabled : messages.disabled} />
                </div>
                <div className={style['settings-page-section']}>
                  <Text className={style['flex-input']} textType={TextType.Label} value={messages.routingType} />
                </div>
                <div className={classnames(style['settings-page-section'], style['sub-text-container'])}>
                  <Text textType={TextType.Description} value={messages.routingTypeActiveShippingLocations} />
                </div>
                <div className={classnames(style['settings-page-section-for-file-upload'], style['sub-text-container'])}>
                  {modalDialogue === IMPORT_IN_PROGRESS_DIALOGUE && file && importProductsComplete ? (
                    <Text textType={TextType.H4} value={messages.fileUploadedLabel} />
                  ) : null}
                  {modalDialogue === IMPORT_IN_PROGRESS_DIALOGUE && file && importProductsStarted ? (
                    <Text
                      formattedMessageValues={{
                        link: (chunks) => (
                          <Button className={style['auto-routing-link']} href='/products' theme='link'>
                            {chunks}
                          </Button>
                        ),
                      }}
                      textType={TextType.Caption}
                      value={messages.productImportStartedAfterSetup}
                    />
                  ) : null}
                  {modalDialogue === CHOOSE_IMPORT_DIALOGUE && file ? (
                    <Button disabled={!file} onClick={onConfirm} theme='primary'>
                      <FormattedMessage {...messages.uploadFileButtonLabel} />
                    </Button>
                  ) : null}
                  {(modalDialogue === CHOOSE_IMPORT_DIALOGUE || modalDialogue === IMPORT_IN_PROGRESS_DIALOGUE) && (
                    <FileUploader
                      className={style['import-pill']}
                      file={file}
                      message={messages.uploadNewCsv}
                      name='productImport'
                      setFile={setFile}
                      setModalDialogue={setModalDialogue}
                      shouldOnlyDisplayFilename={file !== null}
                      theme='link'
                    />
                  )}
                </div>
                <div className={style['settings-page-section']}>
                  <Text className={style['flex-input']} textType={TextType.Label} value={messages.routeToFulfillmentProviderLabel} />
                  <div className={classnames(style['flex-input'])}>
                    <CheckboxSlider
                      checkboxId='use-fulfillment-providers-toggle'
                      checked={shouldUseFulfillmentProviders}
                      onChange={onShouldUseFulfillmentProvidersChange}
                    />
                  </div>
                </div>
                <div className={classnames(style['settings-page-section'], style['sub-text-container'])}>
                  <Text textType={TextType.Description} value={messages.routeToFulfillmentProviderDescription} />
                </div>
                <Text textType={TextType.Label} value={messages.fulfillmentPriority} />
                <RadioCheckList className={classnames(style['fulfillment-priorities'])}>
                  {fulfillmentStrategies.map((opt) => (
                    <div key={opt.id} className={style['fulfillment-priorities-option']}>
                      <RadioCheckListItem
                        className={style['fulfillment-priorities-radio-button']}
                        id={opt.id}
                        isChecked={opt.id === selectedFulfillmentStrategy}
                        onChecked={() => onSelectedFulfillmentStrategyChange(opt.id)}
                      >
                        <Text textType={TextType.Description} value={opt.title} />
                      </RadioCheckListItem>
                      <Text textType={TextType.Caption} value={opt.message} />
                    </div>
                  ))}
                </RadioCheckList>
              </>
            ) : null}
          </div>
        )}
      </div>

      {importLogModalOpen ? <ImportLogModal /> : null}

      {isConfirmEnableSmartRoutingModalOpen ? (
        <ConfirmEnableSmartRouting
          handleContinueEnablingSmartRouting={handleContinueEnablingSmartRouting}
          onClose={closeConfirmEnableSmartRoutingModal}
        />
      ) : null}

      {isConfirmDisableSmartRoutingModalOpen ? (
        <ConfirmDisableSmartRouting
          handleContinueDisablingSmartRouting={handleContinueDisablingSmartRouting}
          onClose={closeConfirmDisableSmartRoutingModal}
        />
      ) : null}
    </Fragment>
  );
};
